import { z } from 'zod';
import { hardeningTransitionV1Schema } from './hardeningConfigurationSchemas';
import { installationManifestSchema, recoveryKindSchema, recoveryStageSchema,
  fleetRecoveryDeviceIdSchema as device, fleetRecoveryIdSchema as id,
  fleetRecoveryUuidSchema as uuid, fleetRecoverySha256Schema as hash,
  fleetRecoveryUint64Schema as uint64, fleetRecoveryUtcSchema as utc,
  fleetRecoverySignatureSchema as signature, fleetRecoveryPathSchema,
  FleetRecoveryPathPolicy, FleetRecoveryCanonicalJson } from '../fleetRecoverySchemas';

export const RECOVERY_V3_DOMAIN = 'playcast-recovery-envelope:v3\n';
export const RECOVERY_V3_DEVICE_DOMAIN = 'playcast-recovery-v3';
export const RECOVERY_QUALIFICATION_DOMAIN = 'playcast-recovery-qualification:v1\n';
export const RECOVERY_V3_EXECUTOR_VERSION = 5;
export const recoveryChannelV3Schema = z.string().min(1).max(128).regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/);
export const installPolicyV3Schema = z.enum(['on-host-entry-or-reboot', 'immediate']);
export const preparationStateV3Schema = z.enum(['discovered', 'downloading', 'verified-waiting', 'installing', 'verifying', 'ready', 'blocked', 'superseded']);
export const expiryDispositionV3Schema = z.enum(['live', 'expired-unadmitted', 'expired-awaiting-reconciliation', 'terminal']);
export const recoveryGenerationV3Schema = uint64.refine(value => value !== '0');
export const recoveryBudgetV3Schema = z.number().int().min(1).max(32);
export const recoveryRootsV3Schema = z.object({ productRoot: fleetRecoveryPathSchema, dataRoot: fleetRecoveryPathSchema }).strict()
  .superRefine((roots, ctx) => {
    if (Object.values(roots).some(root => root.split('\\').length < 3 || /^[a-z]:\\(?:Windows|Users)(?:\\|$)/i.test(root)
      || FleetRecoveryPathPolicy.isRecoveryLocator(root)) || FleetRecoveryPathPolicy.isWithin(roots.productRoot, roots.dataRoot)
      || FleetRecoveryPathPolicy.isWithin(roots.dataRoot, roots.productRoot)) ctx.addIssue({ code: 'custom', message: 'Invalid recovery product roots' });
  });
export interface SignedV3<T> { algorithm: 'Ed25519'; keyId: string; payload: T; signature: string }
export const signedV3Schema = <T extends z.ZodTypeAny>(payload: T) => z.object({
  algorithm: z.literal('Ed25519'), keyId: id, payload, signature,
}).strict();

export const releaseTemplateV3Schema = z.object({ schemaVersion: z.literal(3), templateId: uuid,
  productChannel: recoveryChannelV3Schema, manifest: installationManifestSchema, templateRoots: recoveryRootsV3Schema,
  minExecutorVersion: z.literal(5), maxReboots: recoveryBudgetV3Schema,
  maxOperationSeconds: z.number().int().min(1).max(86400), allowedPurposes: z.array(recoveryKindSchema).min(1).max(11),
  hardeningTransition: hardeningTransitionV1Schema.optional(),
}).strict().superRefine((value, ctx) => {
  if (new Set(value.allowedPurposes).size !== value.allowedPurposes.length || value.allowedPurposes.includes('force-reboot')
    || value.manifest.productVersion === 'recovery-reboot-v4'
    || (value.hardeningTransition !== undefined && (!value.allowedPurposes.includes('rotate-authority')
      || value.manifest.authorityTransition !== undefined))
    || new TextEncoder().encode(FleetRecoveryCanonicalJson.stringify(value)).length > 2 * 1024 * 1024)
    ctx.addIssue({ code: 'custom', message: 'Invalid release template authority or budget' });
});
export const signedReleaseTemplateV3Schema = signedV3Schema(releaseTemplateV3Schema);
export const rootSpecializationV3Schema = z.object({ deviceId: device, bootId: id, generation: uint64,
  productRoot: fleetRecoveryPathSchema, dataRoot: fleetRecoveryPathSchema, inventorySha256: hash, observedAtUtc: utc,
}).strict().superRefine((value, ctx) => {
  if (!recoveryRootsV3Schema.safeParse({ productRoot: value.productRoot, dataRoot: value.dataRoot }).success)
    ctx.addIssue({ code: 'custom', message: 'Invalid specialization roots' });
});
export const backgroundUpdateAssignmentV3Schema = z.object({ schemaVersion: z.literal(3), deviceId: device,
  assignmentId: uuid, assignmentGeneration: recoveryGenerationV3Schema, installedChannel: recoveryChannelV3Schema,
  releaseEnvelopeSha256: hash, releaseEnvelope: signedReleaseTemplateV3Schema,
  installPolicy: z.literal('on-host-entry-or-reboot'), assignedAtUtc: utc, stageByUtc: utc, operationDeadlineUtc: utc,
  maxReboots: recoveryBudgetV3Schema, authorizationId: id, candidateAuthorizationId: uuid.nullable(),
}).strict().superRefine((a, ctx) => {
  const release = a.releaseEnvelope.payload;
  if (a.installedChannel !== release.productChannel || !release.allowedPurposes.includes('upgrade')
    || a.maxReboots > release.maxReboots || Date.parse(a.stageByUtc) <= Date.parse(a.assignedAtUtc)
    || Date.parse(a.operationDeadlineUtc) < Date.parse(a.stageByUtc)
    || Date.parse(a.operationDeadlineUtc) - Date.parse(a.assignedAtUtc) > release.maxOperationSeconds * 1000)
    ctx.addIssue({ code: 'custom', message: 'Assignment exceeds release authority' });
});
export const recoveryCommandV3Schema = z.object({ schemaVersion: z.literal(3), deviceId: device, operationId: uuid,
  generation: recoveryGenerationV3Schema, correlationId: uuid, kind: recoveryKindSchema, installPolicy: installPolicyV3Schema,
  releaseEnvelopeSha256: hash.nullable(), releaseEnvelope: signedReleaseTemplateV3Schema.nullable(),
  specialization: rootSpecializationV3Schema.nullable(), issuedAtUtc: utc, expiresAtUtc: utc, deadlineUtc: utc,
  maxReboots: recoveryBudgetV3Schema, authorizationId: id, candidateAuthorizationId: uuid.nullable(),
}).strict().superRefine((c, ctx) => {
  const bad = () => ctx.addIssue({ code: 'custom', message: 'Command exceeds release authority' });
  if (Date.parse(c.expiresAtUtc) <= Date.parse(c.issuedAtUtc) || Date.parse(c.deadlineUtc) < Date.parse(c.expiresAtUtc)
    || Date.parse(c.deadlineUtc) - Date.parse(c.issuedAtUtc) > 86400000) bad();
  if (c.kind === 'force-reboot') {
    if (c.installPolicy !== 'immediate' || c.releaseEnvelope !== null || c.releaseEnvelopeSha256 !== null
      || c.specialization !== null || c.maxReboots !== 1 || c.candidateAuthorizationId !== null) bad();
    return;
  }
  const r = c.releaseEnvelope?.payload;
  if (!r || !c.releaseEnvelopeSha256 || !r.allowedPurposes.includes(c.kind) || c.maxReboots > r.maxReboots
    || Date.parse(c.deadlineUtc) - Date.parse(c.issuedAtUtc) > r.maxOperationSeconds * 1000) bad();
  if (c.specialization && (c.specialization.deviceId !== c.deviceId || c.specialization.generation !== c.generation)) bad();
  if (c.kind === 'force-upgrade' && c.installPolicy !== 'immediate') bad();
  if (c.kind === 'rotate-authority' && !r?.hardeningTransition) bad();
  if (c.installPolicy !== 'immediate' && c.kind !== 'upgrade' && c.kind !== 'install') bad();
});
const deliveryBase = z.object({ schemaVersion: z.literal(3), deviceId: device, cursor: id });
export const deliveryPayloadV3Schema = z.discriminatedUnion('type', [
  deliveryBase.extend({ type: z.literal('empty') }).strict(),
  deliveryBase.extend({ type: z.literal('assignment'), assignment: backgroundUpdateAssignmentV3Schema }).strict(),
  deliveryBase.extend({ type: z.literal('command'), command: recoveryCommandV3Schema }).strict(),
]).superRefine((d, ctx) => {
  if ((d.type === 'assignment' && d.assignment.deviceId !== d.deviceId) || (d.type === 'command' && d.command.deviceId !== d.deviceId))
    ctx.addIssue({ code: 'custom', message: 'Delivery device mismatch' });
});
export const deliveryV3Schema = signedV3Schema(deliveryPayloadV3Schema);
export const nativeInstallTriggerV3Schema = z.object({ deviceId: device, bootId: id, triggerId: uuid,
  kind: z.enum(['host-entry', 'boot']), lifecycleGeneration: uint64, assignmentId: uuid, observedAtUtc: utc }).strict();
export const recoveryOperatorIntentV3Schema = z.object({ schemaVersion: z.literal(3), idempotencyKey: uuid,
  kind: recoveryKindSchema, targetChannel: recoveryChannelV3Schema.nullable(), installPolicy: installPolicyV3Schema,
  candidateAuthorizationId: uuid.optional(),
  foundationRole: z.enum(['updater', 'rescue', 'baseline']).optional(),
  deadlineUtc: utc, maxReboots: recoveryBudgetV3Schema }).strict().superRefine((i, ctx) => {
    if ((i.candidateAuthorizationId && (i.installPolicy !== 'immediate' || i.kind === 'force-reboot'))
      || (i.foundationRole === 'updater' && i.kind !== 'replace-updater')
      || (i.foundationRole !== undefined && i.foundationRole !== 'updater' && i.kind !== 'replace-rescue')
      || (i.kind === 'force-reboot' && (i.targetChannel !== null || i.maxReboots !== 1 || i.installPolicy !== 'immediate'))
      || (i.kind === 'force-upgrade' && i.installPolicy !== 'immediate')) ctx.addIssue({ code: 'custom', message: 'Invalid force intent' });
  });
export const recoveryCommandAcceptedV3Schema = z.object({ schemaVersion: z.literal(3), deviceId: device,
  operationId: uuid, generation: recoveryGenerationV3Schema, deliveryState: z.enum(['queued', 'delivered', 'running']) }).strict();
export type InstallPolicyV3 = z.infer<typeof installPolicyV3Schema>;
export type PreparationStateV3 = z.infer<typeof preparationStateV3Schema>;
export type ExpiryDispositionV3 = z.infer<typeof expiryDispositionV3Schema>;
export type ReleaseTemplateV3 = z.infer<typeof releaseTemplateV3Schema>;
export type SignedReleaseTemplateV3 = z.infer<typeof signedReleaseTemplateV3Schema>;
export type RootSpecializationV3 = z.infer<typeof rootSpecializationV3Schema>;
export type BackgroundUpdateAssignmentV3 = z.infer<typeof backgroundUpdateAssignmentV3Schema>;
export type RecoveryCommandV3 = z.infer<typeof recoveryCommandV3Schema>;
export type DeliveryPayloadV3 = z.infer<typeof deliveryPayloadV3Schema>;
export type DeliveryV3 = z.infer<typeof deliveryV3Schema>;
export type NativeInstallTriggerV3 = z.infer<typeof nativeInstallTriggerV3Schema>;
export type RecoveryOperatorIntentV3 = z.infer<typeof recoveryOperatorIntentV3Schema>;
export type RecoveryCommandAcceptedV3 = z.infer<typeof recoveryCommandAcceptedV3Schema>;
